import type { JSONContent } from '@tiptap/core';

const isListNode = (n: JSONContent): boolean =>
  n.type === 'bulletList' || n.type === 'orderedList';

/** A paragraph with no visible text — empty, or only hard breaks / whitespace. */
const isBlankParagraph = (n: JSONContent): boolean =>
  n.type === 'paragraph' &&
  (n.content ?? []).every(
    (c) =>
      c.type === 'hardBreak' ||
      (c.type === 'text' && (c.text ?? '').trim() === '')
  );

/** A list item whose blocks are all blank paragraphs. */
const isBlankItem = (n: JSONContent): boolean =>
  n.type === 'listItem' && (n.content ?? []).every(isBlankParagraph);

/** An edge block that carries nothing: a blank paragraph or an emptied list. */
const isBlankBlock = (n: JSONContent): boolean =>
  isBlankParagraph(n) || (isListNode(n) && (n.content ?? []).length === 0);

/** Slice `nodes` down to the run that isn't `blank` at either end. */
const trimEnds = (
  nodes: JSONContent[],
  blank: (n: JSONContent) => boolean
): JSONContent[] => {
  let start = 0;
  let end = nodes.length;
  while (end > start) {
    const n = nodes[end - 1];
    if (n === undefined || !blank(n)) {
      break;
    }
    end -= 1;
  }
  while (start < end) {
    const n = nodes[start];
    if (n === undefined || !blank(n)) {
      break;
    }
    start += 1;
  }
  return nodes.slice(start, end);
};

/**
 * Trim blank paragraphs (both edges) and a dangling empty trailing list item off
 * a TipTap document, so a sent message carries neither a wall of blank lines nor
 * a stray trailing bullet/number. Interior blanks are preserved.
 */
export const trimEmptyEdgeBlocks = (doc: JSONContent): JSONContent => {
  const content = doc.content;
  if (content === undefined) {
    return doc;
  }

  // Shed dangling blank items from a trailing list first (keeping a real list's
  // items); a fully-emptied list then drops out as a blank block below.
  const last = content[content.length - 1];
  const normalized =
    last !== undefined && isListNode(last)
      ? [
          ...content.slice(0, -1),
          { ...last, content: trimEnds(last.content ?? [], isBlankItem) },
        ]
      : content;

  return { ...doc, content: trimEnds(normalized, isBlankBlock) };
};
